import * as React from "react";
import {cx} from "emotion";

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck,
      faSpinner}         from "@fortawesome/free-solid-svg-icons";
import "./uyo-modal.scss";

import {AlertModal}         from "@Common/ui-components/heavy/common/design2/dialogs/alert-modal/alert-modal";
import {isLegacyItem}       from "@Common/logic/item/is-legacy-item";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {Load2}              from "@Common/react/load2";
import {scopedState}        from "@Common/react/scoped-state";
import {UseState}           from "@Common/react/use-state";
import {ModalService3}      from "@Common/ui-components/modal-service-3/modal-service-3";
import {changeNestedValue}  from "@Common/utils/arr-path";
import {chain}              from "@Common/utils/fs";

import {Updater} from "../../../customize-action/xpresso-checkout/updater";

export const UyoModal = ({templateOptions, customizeStatus, file=undefined, item=null, view, gallery, next: rootNext}) => cs(
    consumeContext("apis"),
    consumeContext("routerHistory"),
    consumeContext("systemConstants"),
    ["uploading", ({}, next) => UseState({
        initValue: "Uploading",
        next,
    })],
    ["modal", ({uploading}, next) => ModalService3({
        title: "UPLOAD",
        xdQaName: "UYO Modal",
        width: 675,
        xToClose: !uploading.value,
        ignorePassiveClose: uploading.value,
        rootNext,
        next,
    })],
    ["alertModal", (_, next) => AlertModal({next})],
    ["imageAssetId", ({modal}, next) => next(modal?.params?.imageAssetId ?? 0)],
    ["formData", ({modal}, next) => UseState({
        initValue: {
            templateId: templateOptions.sku.back || templateOptions.sku.front,
            file: file ? file : modal?.params?.file,
        },
        next,
    })],
    ["progressBar", ({}, next) => UseState({
        initValue: 0,
        next,
    })],
    ["validateUpload", ({apis, formData, imageAssetId, progressBar, uploading, systemConstants}, next) => file ? (
        UseState({initValue: file, next})
    ) : (
        Load2({
        _key: imageAssetId || formData.value?.file,
        fetch: async () => {
            const result = (!!imageAssetId) ?
                // if an image asset is provided, validate the existing asset
                await apis.next.gallery.pdf.validateImageAsset({imageAssetId, templateId: templateOptions.sku.front}) :
                // if no image asset is provided, upload the file and validate
                await apis.next.gallery.pdf.validateUpload({
                formData: formData.value,
                postMaxSize: systemConstants.value.UPLOAD_MAX_PHP_FILE_SIZE,
                onUploadProgress: (p) => {
                    uploading.onChange("Uploading")
                    progressBar.onChange(p)
                    p === 100 && uploading.onChange("Processing")
                }
            })
            uploading.onChange(false)
            return result
        },
        next,
    }))],
    ["updater", ({apis, modal}, next) => Updater({
        sku: templateOptions?.sku,
        apis,
        prepareSave: (item) => modal?.params?.prepareSave(item),
        next
    })],
    ({apis, modal, alertModal, validateUpload, formData, progressBar, updater, routerHistory, uploading, systemConstants}) => (
        <div className="uyo-modal-8d3 uyo-modal">
            <div className="body">
                <div className="header" xd-qa-type="text" xd-qa-name="Upload Status">
                    {validateUpload.loading ? (
                        "Validating Upload"
                    ) : validateUpload.value?.message ? (
                        "ERROR: " + validateUpload.value?.message
                    ) : validateUpload.value?.issues.pageCount.status !== "success" ? (
                        "ERROR: PAGE MISMATCH"
                    ) : (
                        "UPLOAD COMPLETE"
                    )}
                </div>
                <div className="main">
                    <div className="left" xd-qa-type="card" xd-qa-name="Expected">
                        <div className="title" xd-qa-type="label" xd-qa-name="Expected">
                            EXPECTED
                        </div>
                        <div className="page-count">
                            <div className="page-count-title" xd-qa-type="label" xd-qa-name="Pages">
                                Pages
                            </div>
                            <div className="expected-pages" xd-qa-type="text" xd-qa-name="Expected Pages">
                                {templateOptions.numOfPages} Pgs
                            </div>
                        </div>
                        <div className="page-size">
                            <div className="page-size-title" xd-qa-type="label" xd-qa-name="Page Size">
                                Page Size
                            </div>
                            <div className="expected-page-size" xd-qa-type="text" xd-qa-name="Expected Page Size">
                                {`${templateOptions.product.finishedWidth + templateOptions.bleed * 2} x ${templateOptions.product.finishedHeight + templateOptions.bleed * 2}`}
                            </div>
                        </div>
                    </div>
                    <div className="middle" xd-qa-type="card" xd-qa-name="Received">
                        <div className="title" xd-qa-type="label" xd-qa-name="Received">
                            RECEIVED
                        </div>
                        <div className="page-count">
                            <div className="page-count-title" xd-qa-type="label" xd-qa-name="Pages">
                                Pages
                            </div>
                            <div className={cx("received-pages", {error: !validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageCount.status !== "success"})}
                                xd-qa-type="text"
                                xd-qa-name="Received Pages"
                            >
                                {validateUpload.value?.message ? (
                                    ""
                                ) : validateUpload.value?.issues.pageCount.status === "success" ? (
                                    <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                                ) : (
                                    validateUpload.value && `${validateUpload.value?.issues.pageCount.provided} Pgs`
                                )}
                            </div>
                        </div>
                        <div className="page-size">
                            <div className="page-size-title" xd-qa-type="label" xd-qa-name="Page Size">
                                Page Size
                            </div>
                            <div className={cx("received-page-size", {error: !validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageSize.status !== "success"})}
                                xd-qa-type="text"
                                xd-qa-name="Received Page Size"
                            >
                                {validateUpload.value?.message ? (
                                    ""
                                ) : validateUpload.value?.issues.pageSize.status === "success" ? (
                                    <div className="validation-icon" xd-qa-type="icon" xd-qa-name="Check"><FontAwesomeIcon className={"icon"} icon={faCheck}/></div>
                                ) : (
                                    validateUpload.value?.issues.pageSize.provided
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="right" xd-qa-type="card" xd-qa-name="Messages">
                        <div className="title-spacer">
                            <div className={cx("page-count-message")} xd-qa-type="text" xd-qa-name="Page Count Message">
                                {validateUpload.value?.message ? (
                                    ""
                                ) : !validateUpload.loading &&
                                    validateUpload.value?.issues.pageCount.messages.map((e, i) => <div key={i} className={cx("message", {
                                        success: !validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageCount.status === "success",
                                        error: !validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageCount.status === "error"
                                    })}>{e}</div>)}
                            </div>
                            <div className={cx("page-size-message")} xd-qa-type="text" xd-qa-name="Page Size Message">
                                {validateUpload.value?.message ? (
                                    ""
                                ) : !validateUpload.loading && <>
                                    {validateUpload.value?.issues.pageSize.messages.map((e, i) => <div key={i} className={cx("message", {success: !validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageSize.status === "success"})}>{e}</div>)}
                                    {!validateUpload.loading && !validateUpload.value?.message && validateUpload.value?.issues.pageSize.status !== "success" &&
                                        <div className="uyo-guide-message">Please refer to the <a
                                            className="link"
                                            xd-qa-type="link"
                                            xd-qa-name="UYO guidelines"
                                            href="https://assets.xpressdocs.com/next_images/help_downloads/UYO_Template_Design_Guide.pdf"
                                            target="_blank"
                                        >
                                            UYO Guidelines
                                        </a></div>
                                    }
                                </>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="footer">
                {validateUpload.loading && (
                    <div className="progress-container">
                        <div
                            className="progress-bar"
                            style={{
                                width: `${progressBar.value}%`,
                            }}
                        >{`${progressBar.value}%`}</div>
                    </div>
                )}
                {!file && <button
                    xd-qa-type="button"
                    xd-qa-name="Reupload"
                    className="reupload"
                    disabled={validateUpload.loading}
                    onClick={async () => {
                        progressBar.onChange(0);
                        uploading.onChange("Uploading");
                        uploadPDF({formData, alertModal, systemConstants})
                    }}
                >
                    {validateUpload.loading ? (
                        <div className="spinner-footer" xd-qa-type="label" xd-qa-name="Loading">
                            <FontAwesomeIcon icon={faSpinner} className="fa-pulse"/>
                        </div>
                    ) : (
                        "Reupload"
                    )}
                </button>}
                <button
                    xd-qa-type="button"
                    xd-qa-name="Proceed"
                    className="proceed"
                    disabled={
                        validateUpload.loading ||
                        validateUpload.value?.message ||
                        validateUpload.value?.issues?.pageCount?.status === "error" ||
                        validateUpload.value?.issues?.pageSize?.status === "error"
                    }
                    onClick={async () => {
                        modal.close()
                        customizeStatus.updating()
                        const result = await apis.next.gallery.pdf.splitPdf(validateUpload.value)
                        const additionalData = (_) => chain(
                            _,
                            (_) => changeNestedValue(_, ["itemThumbnails"], () => [result?.thumbnails[0]]),
                            (_) => changeNestedValue(_, ["uyoMPB"], () => true),
                            (_) => changeNestedValue(_, ["metaData","pdfValidation"], () => result),
                        )
                        view.onChange({enlarged: false, key: 0})
                        gallery.onChange({
                            start: 0,
                            end: gallery.value.end - gallery.value.start,
                            size: gallery.value.size
                        })
                        if (!item) {
                            const newItem = await updater.createCcItem({
                                listingUniqueId: sessionStorage.getItem('listingUniqueId'),
                                additionalData,
                            });
                            await saveItemImage({
                                apis,
                                itemId: newItem.itemId,
                                imageAssetId: validateUpload.value?.imageAssetId
                            });
                            await routerHistory.push('/product-options/order/' + newItem.itemId)
                            customizeStatus.clear()
                        } else if(isLegacyItem(item)) {
                            const newItem = await updater.updateLegacyItem({
                                oldItem: item,
                                additionalData,
                            });
                            await saveItemImage({
                                apis,
                                itemId: newItem.itemId,
                                imageAssetId: validateUpload.value?.imageAssetId
                            });
                            await item.reload()
                            customizeStatus.clear()
                        } else {
                            const newItem = await updater.updateCcItem({
                                oldItem: item,
                                additionalData,
                            });
                            await saveItemImage({
                                apis,
                                itemId: newItem.itemId,
                                imageAssetId: validateUpload.value?.imageAssetId
                            });
                            await item.reload()
                            customizeStatus.clear()
                        }
                    }}
                >
                    {validateUpload.loading ? uploading.value : "Proceed"}
                </button>
            </div>
        </div>
    ),
);

const uploadPDF = ({formData, alertModal, systemConstants}) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf';
    fileInput.style.display = 'none';

    fileInput.addEventListener('change', async function() {
        const file = fileInput.files[0];
        if (file) {
            const fileName = file.name.toLowerCase();
            if (!fileName.endsWith('.pdf')) {
                await alertModal.show({
                    message: "Please select a valid PDF file.",
                });
            }
            else if (file.size > systemConstants?.value?.UPLOAD_MAX_CHUNKED_UPLOAD) {
                await alertModal.show({
                    message: "File size is too large",
                    description: `File size must be under ${parseInt(systemConstants?.value?.UPLOAD_MAX_CHUNKED_UPLOAD) / 1000000} MB`,
                });
            }
            else {
                scopedState(formData, ['file']).onChange(file);
            }
        }
    });

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
}

const saveItemImage = async ({apis, itemId, imageAssetId}) => (
    await apis.next.order.saveImageAssetReferences(itemId, [{
        assetType: "order",
        assetId: imageAssetId,
        orderImageLabel: "Photo 1:",
        assetUrl: ""
    }])
)